import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import PropTypes from 'prop-types';

import { getFeedbackCoaching } from '../../../../../bridge';
import {
  assessFeedback3d,
  isMeaningfulFeedbackText,
} from '../../../../../features/feedbackFacilitator3d/engine';
// programmatic_v2 Spike F port: PLAHER x comment integration for the
// fallback path. The AI pipeline already integrates Likert; this fires
// when the AI errors so the user still gets PLAHER-aware coaching.
import { integrateLikert } from '../../../../../features/feedbackFacilitator3d/engine/likertIntegration';
import { DebouncedTextArea } from '../../../common/DebouncedTextArea';

const AI_FALLBACK_NOTE = 'Showing rule-based coaching because AI coaching is unavailable.';
const AI_PENDING_NOTE = 'Generating coaching...';
const SAVED_NOTE = 'Feedback check saved.';

// v8.1 (2026-06-02): the 3 strength bars (Depth/What-they-did/Balanced)
// were replaced with a COIN checklist driven by the AI inventory. The
// rule-engine pattern-matching that drove the old bars misread crude
// register as balanced tone and undercounted COIN coverage on real
// student comments. The AI inventory is the authoritative signal now;
// the rule engine remains as fallback only.
const COIN_ELEMENTS = [
  { key: 'context_present', label: 'Context', help: 'When or where this happened' },
  { key: 'observation_present', label: 'Observation', help: 'A specific action you saw' },
  { key: 'impact_present', label: 'Impact', help: 'What it meant for the team' },
  { key: 'next_steps_present', label: 'Next Steps', help: 'What to keep doing or change' },
];

// PLAHER 6D summary shown in the coaching panel so the writer sees their own
// ratings alongside the AI feedback (2026-05-19 meeting decision). Reads from
// the teamPlayer survey's question keys (full words). The template uses
// 'Reliable' as the question key; we display it as 'Reliability' per the
// PLAHER naming convention.
const PLAHER_DISPLAY = [
  { questionKey: 'Performance', label: 'Performance' },
  { questionKey: 'Leadership', label: 'Leadership' },
  { questionKey: 'Attitude', label: 'Attitude' },
  { questionKey: 'Harmony', label: 'Harmony' },
  { questionKey: 'Expression', label: 'Expression' },
  { questionKey: 'Reliable', label: 'Reliability' },
];

// ENT (Leading Entrepreneurial Teams) 6-competency summary, shown in the
// coaching panel for the entrepreneurship pilot the same way PLAHER is for
// teamPlayer. Question keys are the entEffectiveness template's peerRanged keys;
// labels follow the competency descriptions (Collaborative Conduct is the
// display label for the CollaborativeDecisionMaking key). (2026-07-01)
const ENT_DISPLAY = [
  { questionKey: 'HighStandards', label: 'High Standards' },
  { questionKey: 'CollaborativeDecisionMaking', label: 'Collaborative Conduct' },
  { questionKey: 'NavigatingComplexity', label: 'Navigating Complexity' },
  { questionKey: 'ActingAsVisionary', label: 'Acting as Visionary' },
  { questionKey: 'Resilience', label: 'Resilience' },
  { questionKey: 'SocialDexterity', label: 'Social Dexterity' },
];

// Templates whose Qualitative peer-feedback item gets the AI facilitator/coaching
// UI. teamPlayer = PLAHER (unchanged); the two ENT templates were added for the
// entrepreneurship pilot (2026-07-01) -- previously ENT surveys silently showed
// NO in-survey coaching because this was hard-coded to 'teamPlayer'. MUST stay in
// sync with the backend copy in server/src/routes/respondents.js.
const FACILITATOR_TEMPLATE_IDS = new Set(['teamPlayer', 'entEffectiveness', 'entEffectivenessTeamDynamics']);
const ENT_TEMPLATE_IDS = new Set(['entEffectiveness', 'entEffectivenessTeamDynamics']);
// Pick the Likert summary display list for the survey's rubric.
const displayForTemplate = (templateId) => (ENT_TEMPLATE_IDS.has(templateId) ? ENT_DISPLAY : PLAHER_DISPLAY);

// A page item gets the AI facilitator/coaching UI (per-teammate Submit-for-AI-
// Review + Send-feedback buttons, and the onAllSent auto-advance) only for these
// templates' Qualitative comment. Exported so SurveyBody gates the page-level
// Next button on the EXACT same predicate: Next is hidden only where this
// auto-advance flow exists, so non-coaching templates keep their Next button.
export const isFacilitatorEligibleItem = (templateId, item) => (
  FACILITATOR_TEMPLATE_IDS.has(templateId)
  && item?.type === 'peerRangedText'
  && item?.key === 'Qualitative'
);

// Extract the rater's own Likert ratings of the target peer, if present in the
// response. Returns undefined when no Likert dimensions are available so the
// backend can omit the field cleanly.
//
// Reads from the actual teamPlayer survey question keys (full words) and
// returns them under the PLAHER 6D single-letter codes that the coaching
// engine consumes. Pre-PLAHER versions of this function read legacy 5D
// keys (COM/FND/CMT/HST/KTR) which never matched the real teamPlayer
// survey response shape, so Likert injection silently never functioned.
const TEMPLATE_TO_PLAHER = {
  Performance: 'P',
  Leadership: 'L',
  Attitude: 'A',
  Harmony: 'H',
  Expression: 'E',
  Reliable: 'R',
};
// ENT: the coaching engine's competency profile keys ARE the survey question
// keys, so the mapping is identity (unlike PLAHER's word->letter recode).
const TEMPLATE_TO_ENT = {
  HighStandards: 'HighStandards',
  CollaborativeDecisionMaking: 'CollaborativeDecisionMaking',
  NavigatingComplexity: 'NavigatingComplexity',
  ActingAsVisionary: 'ActingAsVisionary',
  Resilience: 'Resilience',
  SocialDexterity: 'SocialDexterity',
};
const collectLikert = (response, targetPeerId, templateId) => {
  if (!response || !targetPeerId) return undefined;
  const map = ENT_TEMPLATE_IDS.has(templateId) ? TEMPLATE_TO_ENT : TEMPLATE_TO_PLAHER;
  const out = {};
  Object.entries(map).forEach(([questionKey, code]) => {
    const v = response?.[questionKey]?.[targetPeerId];
    if (typeof v === 'number') {
      out[code] = v;
    } else if (typeof v === 'string' && /^\d+$/.test(v)) {
      out[code] = Number(v);
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
};

// COIN checklist (v8.1): four binary indicators rendered from the AI
// inventory's coin{} field after Submit-for-AI-Review. Replaces the v6.x
// 3-bar strength meter. Green check = present, gray = missing.
function CoinChecklist({ coin }) {
  if (!coin) return null;
  return (
    <div className="border rounded p-2 mt-3 bg-white">
      <div className="small text-uppercase text-muted mb-2">
        COIN coverage
      </div>
      <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
        {COIN_ELEMENTS.map((el) => {
          const present = Boolean(coin[el.key]);
          return (
            <div
              key={el.key}
              className="d-flex align-items-center"
              style={{
                flex: '1 1 calc(50% - 8px)',
                minWidth: 0,
                padding: '6px 8px',
                background: present ? '#e8f5e9' : '#f5f5f5',
                border: `1px solid ${present ? '#a5d6a7' : '#e0e0e0'}`,
                borderRadius: '4px',
              }}
              title={el.help}
            >
              <span
                aria-label={present ? 'present' : 'missing'}
                style={{
                  display: 'inline-block',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: present ? '#2e7d32' : '#bdbdbd',
                  color: '#fff',
                  textAlign: 'center',
                  lineHeight: '18px',
                  fontSize: '12px',
                  fontWeight: 700,
                  marginRight: '8px',
                  flex: '0 0 18px',
                }}
              >
                {present ? '✓' : ''}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="small" style={{ fontWeight: 500 }}>{el.label}</div>
                <div className="small text-muted" style={{ fontSize: '0.75rem' }}>{el.help}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

CoinChecklist.propTypes = {
  coin: PropTypes.shape({
    context_present: PropTypes.bool,
    observation_present: PropTypes.bool,
    impact_present: PropTypes.bool,
    next_steps_present: PropTypes.bool,
  }),
};

CoinChecklist.defaultProps = {
  coin: null,
};

function PeerRangedText({
  templateId,
  surveyId,
  respondentId,
  isPreview,
  data,
  peers,
  response,
  onChanged,
  onAllSent,
}) {
  const [showCoach, setShowCoach] = useState({});
  // Per-stateKey AI coaching record:
  //   { status: 'pending' | 'done' | 'error',
  //     coaching: string,            // text to render (AI when usable, else rules-based)
  //     fallbackUsed: boolean,
  //     dimensions: { substance, structure, tone },
  //     logged: boolean,             // backend confirmed log persistence
  //     message: string | null,      // user-safe note from backend, if any
  //     key: string }                // text the result was computed against
  const [aiCoaching, setAiCoaching] = useState({});

  // v6.2 (2026-05-23 pm): per-teammate finalize state. The 2026-05-19
  // Psychology team meeting + Denis's 2026-05-23 QA testing called for a
  // distinct "Submit Feedback for [Teammate]" button that appears after
  // the first AI review round. Clicking it marks this teammate's feedback
  // as final-for-now and hides the AI-Review + Submit-Feedback buttons.
  // The textarea is intentionally NOT locked here -- locking would
  // require adding a readOnly prop to DebouncedTextArea (out of scope
  // for this change). If the user edits after finalizing, the response
  // is still saved on page-advance; the finalize state is purely a UX
  // signal to the writer that they have committed to this feedback.
  const [finalized, setFinalized] = useState({});

  // 2026-06-05 (McKenna UX): auto-advance when every teammate's feedback
  // is "done". A teammate counts as done iff the user has clicked
  // "Send Feedback to {name}" (finalized) OR has checked the
  // "Not familiar with this team member's behavior" checkbox (textValue
  // === 'N/A'). One-shot via autoAdvanceTriggered so coming back via Back
  // doesn't immediately punt the user forward again.
  const [autoAdvanceTriggered, setAutoAdvanceTriggered] = useState(false);

  // 2026-06-09 (#4a): one-click "Send to all" -- screens every teammate's
  // comment (the AI screen is never skipped) and submits them together, but
  // PAUSES on any comment flagged hostile (AI) or mandatory_rephrase (rules)
  // by surfacing that comment's coaching instead of sending it. sendingAll
  // disables the button + drives a progress note; sendAllNote names the
  // flagged teammates left for the writer to review.
  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllProgress, setSendAllProgress] = useState('');
  const [sendAllNote, setSendAllNote] = useState(null);

  const items = useMemo(
    () => data.filter((item) => item.type === 'peerRangedText'),
    [data],
  );

  // 2026-06-05: auto-advance check. Fires once when every facilitator-
  // eligible teammate is either finalized or marked Not familiar ('N/A').
  // Skips if onAllSent isn't provided (preview / legacy callers).
  useEffect(() => {
    if (autoAdvanceTriggered) return;
    if (typeof onAllSent !== 'function') return;
    if (!peers || peers.length === 0) return;
    const qualItems = items.filter((item) => isFacilitatorEligibleItem(templateId, item));
    if (qualItems.length === 0) return;
    const allDone = qualItems.every((item) => peers.every((p) => {
      const sk = `${item.key}-${p.respondentId}`;
      const tv = (response[item.key] || {})[p.respondentId] || '';
      return finalized[sk] || tv === 'N/A';
    }));
    if (allDone) {
      setAutoAdvanceTriggered(true);
      onAllSent();
    }
  }, [finalized, response, peers, items, templateId, autoAdvanceTriggered, onAllSent]);

  // Internal rules-based assessment per stateKey -- used to drive strength
  // bars and provide a fallback coaching string. Never rendered as raw
  // dimension labels in the student UI.
  const feedbackResults = useMemo(() => {
    const nextResults = {};
    items.forEach((item) => {
      if (!isFacilitatorEligibleItem(templateId, item)) {
        return;
      }
      peers.forEach((participant) => {
        const stateKey = `${item.key}-${participant.respondentId}`;
        const textValue = (response[item.key] || {})[participant.respondentId] || '';
        nextResults[stateKey] = assessFeedback3d(textValue);
      });
    });
    return nextResults;
  }, [items, peers, response, templateId]);

  const requestCoaching = useCallback(async (stateKey, item, participant, textValue) => {
    setAiCoaching((prev) => ({
      ...prev,
      [stateKey]: {
        status: 'pending',
        key: textValue,
        fallbackUsed: false,
        coaching: '',
        inventoryCoin: null,
        stage: null,
        logged: false,
        message: null,
      },
    }));

    const localAssessment = assessFeedback3d(textValue);
    const plaher = collectLikert(response, participant.respondentId, templateId);
    // Privacy (2026-07-01): send the team roster so the server pseudo-anonymizes
    // teammate names OUT of the comment before it reaches the model API (redact ->
    // model -> restore, all deterministic; see feedbackCoaching/anonymizer.js). The
    // comment still goes to the cloud model under informed consent -- this is
    // defense-in-depth name-stripping, NOT data residency. Includes first, last,
    // and "First Last" for every peer (the writer may name any teammate).
    const rosterNames = (peers || [])
      .flatMap((p) => [p.firstName, p.lastName, `${p.firstName || ''} ${p.lastName || ''}`.trim()])
      .filter((s) => typeof s === 'string' && s.trim().length >= 2);
    const payload = {
      comment: textValue,
      names: rosterNames.length ? [...new Set(rosterNames)] : undefined,
      likert: plaher,
      routing: localAssessment.routing_label,
      // Phase B.3: send the local rules-engine safety level so the server can
      // short-circuit on ready_to_send + safety_level=2 without calling the AI.
      ruleBasedSafetyLevel: localAssessment?.safety?.level,
      preview: Boolean(isPreview),
      surveyId: surveyId || undefined,
      respondentId: respondentId || undefined,
      templateId: templateId || undefined,
      itemKey: item?.key || undefined,
      targetPeerId: participant?.respondentId || undefined,
    };
    const useAuth = !isPreview;

    const { response: aiResponse, error } = await getFeedbackCoaching(payload, useAuth);

    if (error || !aiResponse) {
      // Network or server error: surface rules-based coaching so the student
      // is never stranded. logged=false so the "saved" note stays hidden.
      // v8.1: no COIN checklist on the fallback path -- the rule engine
      // doesn't produce the AI inventory's coin field.
      // programmatic_v2 Spike F: when PLAHER ratings are available, also
      // run the Likert x comment integration and append its diagnosis so
      // the fallback isn't blind to rating-vs-prose mismatches.
      const fallbackBase = localAssessment.coaching || '';
      const likertResult = integrateLikert(textValue, plaher);
      const fallbackCoaching = likertResult
        ? `${fallbackBase}\n\n${likertResult.coaching}`.trim()
        : fallbackBase;
      // 2026-06-09: build the record then set + RETURN it, so the Send-to-all
      // (#4a) loop can read the fresh result synchronously after awaiting this
      // call (React state set here is async, so the closure's aiCoaching is
      // stale -- the returned record is the reliable source). isHostile:false
      // on the AI-error path: we couldn't AI-screen, the rule-based fallback
      // (and feedbackResults mandatory_rephrase) is the safety net.
      const errRec = {
        status: 'error',
        fallbackUsed: true,
        coaching: fallbackCoaching,
        inventoryCoin: null,
        stage: null,
        isHostile: false,
        logged: false,
        message: AI_FALLBACK_NOTE,
        key: textValue,
      };
      setAiCoaching((prev) => ({ ...prev, [stateKey]: errRec }));
      return errRec;
    }

    const fallbackUsed = Boolean(aiResponse.fallbackUsed);
    const aiText = typeof aiResponse.coaching === 'string' ? aiResponse.coaching : '';
    // v8.1 (2026-06-02): the rule-engine dimensions field is gone from the
    // student-facing UI -- the v8 AI inventory's coin{} field is the
    // authoritative signal for COIN coverage now. We still surface the
    // safety-screen verdict for the HOSTILE banner (unchanged from v6.3).
    const inventoryCoin = aiResponse?.inventory?.coin || null;
    const stage = typeof aiResponse.stage === 'string' ? aiResponse.stage : null;
    const isHostile = Boolean(aiResponse.isHostile);
    const screenLabel = typeof aiResponse.screenLabel === 'string' ? aiResponse.screenLabel : null;
    const doneRec = {
      status: 'done',
      fallbackUsed,
      coaching: fallbackUsed ? (aiResponse?.rulesBased?.coaching || localAssessment.coaching || '') : aiText,
      inventoryCoin,
      stage,
      isHostile,
      screenLabel,
      logged: Boolean(aiResponse.logged),
      message: aiResponse.message || (fallbackUsed ? AI_FALLBACK_NOTE : null),
      key: textValue,
    };
    // 2026-06-09: set + RETURN (see the AI-error branch note) so Send-to-all
    // can read this comment's fresh isHostile verdict right after awaiting.
    setAiCoaching((prev) => ({ ...prev, [stateKey]: doneRec }));
    return doneRec;
  }, [isPreview, response, respondentId, surveyId, templateId]);

  // v6.2 (2026-05-23 pm): renamed from toggleCoach. The prior version was a
  // literal toggle: pressing the button while the panel was open CLOSED it,
  // and a second press re-opened it -- which is why Denis reported the
  // "needs two presses to make it work, after AI feedback was provided"
  // behavior. New semantics: pressing the button ALWAYS keeps the panel
  // open. If the text has changed since the last review, fresh coaching is
  // requested. If the text is identical to the cached result, it's a no-op
  // (panel stays open, no re-run). The user advances via the new
  // "Submit Feedback for Teammate" button instead of closing this panel.
  const requestCoachingClick = useCallback((stateKey, item, participant, textValue) => {
    setShowCoach((prev) => ({ ...prev, [stateKey]: true }));
    if (!isMeaningfulFeedbackText(textValue)) return;
    const cur = aiCoaching[stateKey];
    // Cache hit: same text already requested and either in flight or done.
    if (cur && cur.key === textValue && (cur.status === 'pending' || cur.status === 'done')) {
      return;
    }
    requestCoaching(stateKey, item, participant, textValue);
  }, [aiCoaching, requestCoaching]);

  // 2026-06-09 (#4a): "Send to all". Screens every not-yet-finalized,
  // meaningful comment (reusing a cached screen when the text is unchanged,
  // else running a fresh one -- the AI screen is NEVER skipped), then submits
  // the clean ones together. A comment flagged hostile (AI) or
  // mandatory_rephrase (rule engine) is NOT auto-sent: its coaching panel is
  // opened so the writer reviews it and sends via that teammate's own button.
  const sendAll = useCallback(async () => {
    setSendingAll(true);
    setSendAllNote(null);
    setSendAllProgress('');

    const targets = [];
    items.forEach((item) => {
      if (!isFacilitatorEligibleItem(templateId, item)) return;
      peers.forEach((participant) => {
        const sk = `${item.key}-${participant.respondentId}`;
        const tv = (response[item.key] || {})[participant.respondentId] || '';
        if (finalized[sk]) return; // already submitted
        if (tv === 'N/A' || !isMeaningfulFeedbackText(tv)) return; // nothing to send
        targets.push({
          sk, item, participant, tv,
        });
      });
    });

    const flaggedNames = [];
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      setSendAllProgress(`Screening ${i + 1} of ${targets.length}...`);
      let rec = aiCoaching[t.sk];
      if (!(rec && rec.key === t.tv && rec.status === 'done')) {
        // eslint-disable-next-line no-await-in-loop
        rec = await requestCoaching(t.sk, t.item, t.participant, t.tv);
      }
      const ruleFlagged = Boolean(feedbackResults[t.sk]?.safety?.mandatory_rephrase);
      const aiHostile = Boolean(rec && rec.isHostile);
      if (aiHostile || ruleFlagged) {
        flaggedNames.push(t.participant.firstName);
        setShowCoach((prev) => ({ ...prev, [t.sk]: true }));
      } else {
        setFinalized((prev) => ({ ...prev, [t.sk]: true }));
      }
    }

    setSendingAll(false);
    setSendAllProgress('');
    if (flaggedNames.length) {
      setSendAllNote(
        `Sent the rest. Please review the flagged feedback for ${flaggedNames.join(', ')} below `
        + 'before sending -- it may read as harsh. Revise it, then use that teammate\'s own Send button.',
      );
    }
  }, [items, peers, response, templateId, finalized, aiCoaching, feedbackResults, requestCoaching]);

  if (items.length === 0) {
    return null;
  }

  // 2026-06-09 (#4a): only show "Send to all" when 2+ teammates still have a
  // meaningful, not-yet-submitted comment -- batching is the whole point, and
  // a single remaining comment is just the per-teammate button.
  const sendAllPendingCount = items.reduce((acc, item) => {
    if (!isFacilitatorEligibleItem(templateId, item)) return acc;
    return acc + peers.filter((p) => {
      const sk = `${item.key}-${p.respondentId}`;
      const tv = (response[item.key] || {})[p.respondentId] || '';
      return !finalized[sk] && tv !== 'N/A' && isMeaningfulFeedbackText(tv);
    }).length;
  }, 0);
  const showSendAll = sendAllPendingCount >= 2;

  const aiInPlay = items.some((item) => isFacilitatorEligibleItem(templateId, item));

  return (
    <>
      {aiInPlay && (
        <div className="alert alert-info small mx-3 mt-3" role="note" data-testid="ai-consent-notice">
          <strong>AI feedback notice.</strong>
          {' '}
          When you choose &quot;Submit for AI Review,&quot; your written feedback is sent to OpenAI
          (a third-party AI service based in the United States). It is used to generate coaching
          suggestions for you and to help write your teammate&apos;s individual feedback report. Before
          your feedback is sent, an automated step detects and removes names; this removal is highly
          reliable but not perfect, so on rare occasions a name may still be included. Your coaching
          suggestions are shown only to you, and your teammate&apos;s report never identifies who wrote
          which feedback. This is a pilot feature.
        </div>
      )}
      {items.map((item) => (
        <div key={`peer-ranged-text-${item.key}`}>
          <div className="p-3">{ReactHtmlParser(item.content)}</div>
          {peers.map((participant) => {
            const stateKey = `${item.key}-${participant.respondentId}`;
            const textValue = (response[item.key] || {})[participant.respondentId] || '';
            const isFacilitatorEligible = isFacilitatorEligibleItem(templateId, item);
            const feedbackResult = feedbackResults[stateKey];
            const canDisplayFeedback = isFacilitatorEligible && isMeaningfulFeedbackText(textValue);

            return (
              <div key={`peer-ranged-text-${item.key}-${participant.respondentId}`}>
                <DebouncedTextArea
                  originalValue={textValue}
                  scoringKey={item.key}
                  respondentId={participant.respondentId}
                  content={`${participant.firstName} ${participant.lastName}`}
                  onChanged={(key, id, value) => onChanged({ [key]: { [id]: value } })}
                  unfamiliarCheckbox
                />
                {isFacilitatorEligible && canDisplayFeedback && feedbackResult && (
                  <div className="px-3 pb-2">
                    {feedbackResult.safety?.mandatory_rephrase && (
                      <div className="alert alert-danger py-2 mb-2 small" role="alert">
                        This feedback needs to be rephrased before it can be shared.
                      </div>
                    )}
                    {!finalized[stateKey] && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => requestCoachingClick(stateKey, item, participant, textValue)}
                        disabled={!canDisplayFeedback}
                      >
                        Submit for AI Review
                      </Button>
                    )}
                    {/* v6.2: per-teammate "Submit Feedback" button appears
                        after the first AI review round has resolved
                        (status='done' covers both real coaching and the
                        B.3 short-circuit "ready to submit" case). Clicking
                        it finalizes this teammate's feedback.
                        2026-06-05 (McKenna UX): also unlock on
                        status='error' so AI outages don't trap users;
                        the fallback coaching is still shown with the
                        AI_FALLBACK_NOTE. */}
                    {!finalized[stateKey]
                      && aiCoaching[stateKey]
                      && (aiCoaching[stateKey].status === 'done'
                          || aiCoaching[stateKey].status === 'error') && (() => {
                      // 2026-06-06: cap the displayed first name so the
                      // button doesn't overflow the card on very long
                      // names. Bootstrap buttons are white-space: nowrap
                      // by default, so a name like "Maria-Jose Carlos"
                      // would otherwise push past the card's right edge
                      // (worst on mobile). title= keeps the full name
                      // available on hover + for screen readers.
                      const MAX_NAME_LENGTH = 20;
                      const rawName = participant.firstName || '';
                      const displayName = rawName.length > MAX_NAME_LENGTH
                        ? `${rawName.slice(0, MAX_NAME_LENGTH)}…`
                        : rawName;
                      return (
                        <Button
                          size="sm"
                          variant="primary"
                          title={rawName}
                          onClick={() => setFinalized((prev) => ({ ...prev, [stateKey]: true }))}
                        >
                          {/* v6.6 (2026-05-24): "Send Feedback to {firstname}".
                              2026-06-08: prefixed "anonymous" per the Psychology
                              test session -- "send to {name}" read as a direct,
                              attributed message; this reinforces that delivery
                              is anonymous to the recipient. */}
                          Send anonymous feedback to
                          {' '}
                          {displayName}
                        </Button>
                      );
                    })()}
                    {finalized[stateKey] && (
                      <div className="small text-success mt-1" role="status">
                        <strong>&#10003; Feedback submitted</strong>
                        {' '}
                        for
                        {' '}
                        {participant.firstName}
                        .
                      </div>
                    )}
                    {showCoach[stateKey] && (() => {
                      const ai = aiCoaching[stateKey];
                      const isPending = !ai || ai.status === 'pending';
                      // Resolved state: AI coaching unless fallbackUsed.
                      const coachingText = !isPending ? ai.coaching : '';
                      const showFallbackNote = !isPending
                        && (ai.status === 'error' || (ai.status === 'done' && ai.fallbackUsed));
                      const inventoryCoin = !isPending ? (ai.inventoryCoin || null) : null;
                      const stage = !isPending ? (ai.stage || null) : null;
                      const showSaved = !isPending && ai.logged;
                      // PLAHER Likert summary -- the writer's own ratings shown
                      // alongside the AI feedback (2026-05-19 meeting decision).
                      const likertCells = displayForTemplate(templateId).map((d) => ({
                        label: d.label,
                        value: response?.[d.questionKey]?.[participant.respondentId],
                      }));
                      const anyRated = likertCells.some(
                        (x) => x.value !== undefined && x.value !== null && x.value !== '',
                      );
                      return (
                        <div className="border rounded p-3 mt-2 bg-light">
                          <div className="small text-uppercase text-muted mb-2">Coaching</div>
                          {/* 2026-06-08: the writer-facing "Tone flagged: HOSTILE"
                              banner was REMOVED per the Psychology test session --
                              without context it confused writers. Hostility
                              detection stays INTERNAL: ai.isHostile is still
                              computed and submitted to the backend (see the
                              isHostile field in the submit payload), so the admin
                              HostileCommentsAdmin view and recipient-filtering are
                              unaffected. The writer still receives the model's
                              de-escalation coaching below. */}
                          {anyRated && (
                            <div className="mb-3">
                              <div className="small text-muted mb-1">
                                Your ratings for this teammate:
                              </div>
                              <div className="d-flex flex-wrap" style={{ gap: '0.4rem' }}>
                                {likertCells.map((x) => (
                                  <span
                                    key={x.label}
                                    className="badge bg-white text-dark border"
                                  >
                                    {x.label}
                                    :&nbsp;
                                    {x.value !== undefined && x.value !== null && x.value !== ''
                                      ? `${x.value}/5`
                                      : '—'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {isPending ? (
                            <div className="small text-muted fst-italic" aria-live="polite">
                              {AI_PENDING_NOTE}
                            </div>
                          ) : (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {coachingText}
                            </div>
                          )}
                          {showFallbackNote && (
                            <div className="small text-muted mt-2" aria-live="polite">
                              {ai.message || AI_FALLBACK_NOTE}
                            </div>
                          )}
                          {/* v8.1: COIN checklist from AI inventory.
                              Skipped on hostile (the hostile redirect is
                              the priority) and ready (everything is already
                              present). */}
                          {!isPending && !ai.isHostile && stage !== 'ready' && inventoryCoin && (
                            <CoinChecklist coin={inventoryCoin} />
                          )}
                          {showSaved && (
                            <div className="small text-muted mt-2" aria-live="polite">
                              {SAVED_NOTE}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {/* 2026-06-09 (#4a): one-click "Send to all". Screens every comment first
          (never skips the AI review) and submits the clean ones together;
          comments flagged hostile/needs-rephrase are surfaced for the writer
          rather than auto-sent. The per-teammate Send buttons above still work
          for sending one at a time. */}
      {showSendAll && (
        <div className="px-3 pb-2">
          <Button
            variant="primary"
            onClick={sendAll}
            disabled={sendingAll}
            aria-live="polite"
          >
            {sendingAll
              ? (sendAllProgress || 'Sending…')
              : 'Send anonymous feedback to all teammates'}
          </Button>
          <div className="small text-muted mt-1">
            Each comment is AI-reviewed before it is sent. You can still send to
            teammates one at a time above.
          </div>
        </div>
      )}
      {/* 2026-06-09: the flagged-review note is rendered OUTSIDE the showSendAll
          block on purpose. After send-all holds a comment back, only one comment
          remains pending, so showSendAll flips false and would otherwise hide
          this note immediately -- exactly when the writer most needs to see which
          teammate's feedback was held back. (Caught by the send-all e2e.) */}
      {sendAllNote && (
        <div className="px-3 pb-3">
          <div className="alert alert-warning py-2 mb-0 small" role="alert">
            {sendAllNote}
          </div>
        </div>
      )}
    </>
  );
}

PeerRangedText.propTypes = {
  templateId: PropTypes.string,
  surveyId: PropTypes.string,
  respondentId: PropTypes.string,
  isPreview: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  peers: PropTypes.arrayOf(PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    respondentId: PropTypes.string,
  })),
  response: PropTypes.object,
  onChanged: PropTypes.func,
  onAllSent: PropTypes.func,
};

PeerRangedText.defaultProps = {
  templateId: '',
  surveyId: '',
  respondentId: '',
  isPreview: false,
  data: [],
  peers: [
    { firstName: 'Peer', lastName: 'A', respondentId: 'A' },
    { firstName: 'Peer', lastName: 'B', respondentId: 'B' },
    { firstName: 'Peer', lastName: 'C', respondentId: 'C' },
  ],
  response: {},
  onChanged: () => {},
  onAllSent: null,
};

export default PeerRangedText;
